/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Imgix from 'react-imgix';
import useStyles from './style';
import Button from '../../UI/Button';
import useWindowSize from '../../../helpers/hooks/useWindowSize';
import { links as allLinks } from './links';
import { data } from './mockDatoData';
import { useTranslation } from 'react-i18next';

const LinkDrawer: React.FC<{
  name: string;
  size: string;
  rows: number;
  index?: number;
  offset?: boolean;
  chatOffline?: boolean;
}> = ({ index, name, offset, size, rows = 4, chatOffline = false }) => {
  const { t } = useTranslation('common', { keyPrefix: 'links' });
  const links = allLinks[name];
  const images = data?.allComponents?.find?.(
    ({ componentName }) => componentName === name
  )?.images;
  const [activeLink, setActiveLink] = useState(links[0].imageName);
  const [imageHref, setImageHref] = useState('');
  const [offsetWidth, setOffsetWidth] = useState(0);
  const { width = 0 } = useWindowSize();
  const imgs = useMemo(() => {
    // format images object for easier use when mapping
    const cleanImages: Record<string, { url: string; alt: string }> = {};
    images?.forEach?.(i => {
      cleanImages[i.imageName] = { url: i.image.url, alt: i.image.alt };
    });
    return cleanImages;
  }, [images, links]);

  const isSmall = size === 'small';
  const {
    visibleDrawer,
    linkDrawer,
    linkDrawerWrapper,
    smallDrawer,
    drawerImgWrapper,
    drawerLinks,
    smallLinks,
    linkWrapper,
    smallLinkWrapper,
    imageLink,
  } = useStyles();

  useEffect(() => {
    const filteredLinks = links.filter(
      link => link.imageName === activeLink
    )[0];
    setImageHref(filteredLinks.to);
  }, [activeLink]);

  useEffect(() => {
    // update offset of links when width changes
    const handleLoad = () => {
      if (offset) {
        // get the nav
        const navLink = document.getElementById(`nav-link-for-${name}`);
        const bounds = navLink?.getBoundingClientRect() || {
          right: 0,
          width: 0,
        };

        setOffsetWidth(bounds?.right - bounds?.width);
      }
    };

    window.addEventListener('load', handleLoad);

    if (document.readyState === 'complete') {
      handleLoad();
    }

    return () => {
      setOffsetWidth(0);
      window.removeEventListener('load', handleLoad);
    };
  }, [width]);

  return (
    <div
      css={css`
        ${linkDrawer}
        ${visibleDrawer}
        ${isSmall ? smallDrawer : null}
      `}
    >
      <div css={linkDrawerWrapper}>
        {!isSmall && (
          <div css={drawerImgWrapper}>
            {index === 3 ? (
              <Link id="nav-link-setup" href="/cove-install/">
                {imgs?.[activeLink]?.url && (
                  // @ts-expect-error - Imgix type is incorrect
                  <Imgix
                    src={imgs?.[activeLink]?.url}
                    sizes="366px"
                    imgixParams={{ w: '366px', h: '149px' }}
                    htmlAttributes={{
                      alt: imgs?.[activeLink]?.alt,
                      loading: 'lazy',
                    }}
                    disableQualityByDPR
                  />
                )}
                <p css={imageLink}>See how easy it is to install Cove</p>
              </Link>
            ) : (
              <Link href={imageHref} underline="none">
                {imgs?.[activeLink]?.url && (
                  // @ts-expect-error - Imgix type is incorrect
                  <Imgix
                    src={imgs?.[activeLink]?.url}
                    sizes="366px"
                    imgixParams={{ w: '366px', h: '149px' }}
                    htmlAttributes={{
                      alt: imgs?.[activeLink]?.alt,
                      loading: 'lazy',
                    }}
                    disableQualityByDPR
                  />
                )}
              </Link>
            )}
          </div>
        )}
        <div
          css={css`
            ${drawerLinks}
            ${isSmall ? smallLinks : null}
          `}
          style={{
            height: `${36 * rows}px`,
            ...(width >= 1150 && offset
              ? {
                  position: 'fixed',
                  left: `${offsetWidth}px`,
                  transform: 'translateX(calc(-50% + 35px))',
                  maxWidth: 'unset',
                }
              : {}),
          }}
        >
          {links.map((l, i) => {
            const { Component } = l;
            if (Component) {
              return <Component key={`link-drawer-${i}`} />;
            }
            const translationKey = l.translationKey;
            const label = t(translationKey, { defaultValue: l.label });

            return (
              <div
                id={``}
                css={css`
                  ${linkWrapper}
                  ${isSmall ? smallLinkWrapper : null}
                `}
                key={`${l.imageName}-${i}`}
                onMouseOver={() => setActiveLink(l.imageName)}
              >
                {l.to ? (
                  <Link
                    id={`nav-link-to-${l.label
                      .toLowerCase()
                      .replaceAll(' ', '-')}`}
                    href={l.to}
                    className="text-base"
                  >
                    {label}
                  </Link>
                ) : (
                  <Button
                    id={`nav-link-to-${l.label
                      .toLowerCase()
                      .replaceAll(' ', '-')}`}
                    style={l?.buttonStyle || 'outlined'}
                    disabled={l?.id === 'chat' && chatOffline}
                    cypressId="link-drawer-chat-button"
                    chatButton={l?.id === 'chat'}
                  >
                    {label}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LinkDrawer;
